import PropTypes from 'prop-types';
import React, { useState } from 'react';
import I18n from 'utils/I18n';
import Button, { LinkButton } from '@strava/ui/Button';
import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  indexProps,
  cursorDataProps,
  viewingAthleteProps
} from '../../../props/feed-prop-types';

import styles from './index.scss';

import GoalEntry from '../GoalEntry';

const PerformanceGoalMultiView = ({
  entity,
  viewingAthlete,
  intersectionObserverRef,
  cursorData,
  index,
  goalEntries
}) => {
  const [showAllEntries, setShowAllEntries] = useState(false);

  const { entityState } = useFeedEntityContext();
  const cardMargin = index > 0 ? styles.cardMargin : '';
  const firstGoalEntry = goalEntries[0];
  const nextFiveGoalEntries = goalEntries.slice(1, 6);
  const restOfGoalEntries = goalEntries.slice(6);
  const joinable = goalEntries.every((entry) => {
    if (viewingAthlete.id === entry.athlete_id.toString()) {
      return false;
    }
    return true;
  });

  return (
    /* web-feed-4-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */
    <div
      className={`web-feed-4-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
      data-updated-at={cursorData.updated_at}
      data-rank={cursorData.rank}
      data-testid="web-feed-entry"
      ref={intersectionObserverRef}
    >
      <div className={styles.entryHeader}>
        <div className={styles.firstGoalEntry}>
          <GoalEntry
            iconSrc={`/images/performance_goals/${firstGoalEntry.goal_type}-goal-45.png`}
            iconUrl={`/athletes/${firstGoalEntry.athlete_id}`}
            iconAlt={I18n.t(
              'components.feed.performance_goal_created_multi_view.goal_logo',
              {
                goal_type: firstGoalEntry.goal_type
              }
            )}
            description={firstGoalEntry.description}
          />
          {joinable && (
            <LinkButton
              variant="default"
              className={styles.joinBtn}
              href={firstGoalEntry.goal_path}
              data-testid="join-btn"
              onClick={() => {
                entityState.trackAnalytics({
                  action: 'click',
                  element: `${entity.toLowerCase()}_join`
                });
              }}
            >
              {I18n.t(
                'components.feed.performance_goal_created_multi_view.join_goal'
              )}
            </LinkButton>
          )}
        </div>
      </div>

      <div
        className={styles.entryBody}
        id={firstGoalEntry.athlete_feed_entry_id}
      >
        <ul>
          {/* Show up to the next 5 goal entries */}
          {nextFiveGoalEntries.map((entry) => (
            <li key={entry.goal_id}>
              <GoalEntry
                iconSrc={entry.athlete_avatar_url}
                iconUrl={`/athletes/${entry.athlete_id}`}
                iconAlt={entry.athlete_display_name}
                description={entry.description}
              />
            </li>
          ))}
          {/* Remaining goal entries are hidden until show all button is clicked */}
          {restOfGoalEntries.map((entry) => (
            <li
              key={entry.goal_id}
              className={showAllEntries ? styles.show : styles.hidden}
              data-testid="rest-of-goals"
            >
              <GoalEntry
                iconSrc={entry.athlete_avatar_url}
                iconUrl={`/athletes/${entry.athlete_id}`}
                iconAlt={entry.athlete_display_name}
                description={entry.description}
              />
            </li>
          ))}
          {!showAllEntries && goalEntries.length > 5 && (
            <Button
              onClick={() => {
                setShowAllEntries(true);
              }}
              variant="text"
              data-testid="show-all-entries"
              className={styles.showAllEntries}
              type="button"
            >
              {I18n.t(
                'components.feed.performance_goal_created_multi_view.show_all_entries',
                {
                  count: goalEntries.length
                }
              )}
            </Button>
          )}
        </ul>
      </div>
    </div>
  );
};

PerformanceGoalMultiView.propTypes = {
  entity: PropTypes.oneOf(['PerformanceGoalCreated']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  intersectionObserverRef: PropTypes.func.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  goalEntries: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      athlete_feed_entry_id: PropTypes.number.isRequired,
      athlete_id: PropTypes.number.isRequired,
      goal_path: PropTypes.string.isRequired,
      goal_type: PropTypes.string.isRequired,
      goal_id: PropTypes.number.isRequired,
      athlete_avatar_url: PropTypes.string.isRequired,
      athlete_member_type: PropTypes.string.isRequired,
      athlete_display_name: PropTypes.string.isRequired
    })
  ).isRequired
};

export default PerformanceGoalMultiView;
